import { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useLang } from "../i18n/LanguageContext";

// Accuracy (Brian's field finding, 2026-07-14 — "sometimes a random barcode"):
// 1) decode ONLY real retail barcode formats (stray patterns can't misread
//    as QR/Aztec/etc. anymore), and 2) require the SAME code on two
//    consecutive reads before accepting — one glitchy frame can't fire.
const RETAIL_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
];

export default function BarcodeScannerModal({ onScan, onClose }) {
  const { t } = useLang();
  const scannerRef = useRef(null);
  const closingRef = useRef(false);
  const lastReadRef = useRef({ code: null, count: 0 });
  const regionId = "barcode-scanner-region";

  // The camera must be FULLY stopped before React unmounts this modal:
  // unmounting while html5-qrcode is still tearing down its <video> element
  // throws (removeChild on a missing node) and white-screens the whole app.
  async function stopScanner() {
    const scanner = scannerRef.current;
    if (!scanner) return;
    scannerRef.current = null;
    try { await scanner.stop(); } catch { /* already stopped */ }
    try { scanner.clear(); } catch { /* region already gone */ }
  }

  useEffect(() => {
    const scanner = new Html5Qrcode(regionId);
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 250, height: 150 }, formatsToSupport: RETAIL_FORMATS },
      async (decodedText) => {
        if (closingRef.current) return; // ignore extra reads while closing
        // Double-read confirmation: accept only after the same code twice.
        const last = lastReadRef.current;
        lastReadRef.current = decodedText === last.code
          ? { code: decodedText, count: last.count + 1 }
          : { code: decodedText, count: 1 };
        if (lastReadRef.current.count < 2) return;
        closingRef.current = true;
        await stopScanner();
        onScan(decodedText);
      },
      () => { /* ignore per-frame scan failures */ }
    ).catch(async (err) => {
      await stopScanner();
      // Camera permission blocked (customer tapped "Block" once — the browser
      // remembers): raw NotAllowedError means nothing to a store owner, so
      // show the bilingual how-to-unblock steps instead (Brian, 2026-08-31).
      const msg = String(err);
      if (/NotAllowed|Permission denied|PermissionDenied/i.test(msg)) {
        alert(t("cameraDenied"));
      } else {
        alert(t("cameraError", msg));
      }
      onClose();
    });

    return () => { stopScanner(); };
    // Empty deps on purpose: the camera must start exactly once per modal
    // open. Re-running on every parent re-render (the old [onScan, onClose]
    // deps) restarted the camera mid-teardown and crashed the app.
  }, []);

  async function handleClose() {
    if (closingRef.current) return;
    closingRef.current = true;
    await stopScanner();
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content scanner-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>
        <h3>{t("scanTitle")}</h3>
        <div id={regionId} className="scanner-region" />
        <p className="scanner-hint">{t("scanHint")}</p>
      </div>
    </div>
  );
}
